import React, { useState, useEffect } from 'react';
import { Card, Dropdown, Button, Form, Modal } from 'react-bootstrap';
import {
    fetchBenches,
    createBench,
    updateBench,
    deleteBench,
} from '../../api/index.js';

const BenchControlPanel = () => {
    const [benches, setBenches] = useState([]);
    const [showDeleteBenchModal, setShowDeleteBenchModal] = useState(false);
    const [newBenchForm, setNewBenchForm] = useState({ bench_number: '', location: '', latitude: '', longitude: '' });
    const [selectedBench, setSelectedBench] = useState(null);
    const [form, setForm] = useState({ bench_number: '', location: '', latitude: '', longitude: '' });

    const fetchAllBenches = async () => {
        try {
            const { data } = await fetchBenches();
            setBenches(data);
        } catch (err) {
            console.error('Failed to fetch benches:', err);
        }
    };

    useEffect(() => {
        fetchAllBenches();
    }, []);

    const handleSelectBench = (benchId) => {
        const bench = benches.find((b) => b._id === benchId);
        setSelectedBench(bench);
        setForm(bench);
    };

    const handleInputChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleNewBenchInputChange = (e) => {
        setNewBenchForm({ ...newBenchForm, [e.target.name]: e.target.value });
    };

    const handleCreateBench = async () => {
        try {
            await createBench({
                bench_number: Number(newBenchForm.bench_number),
                latitude: Number(newBenchForm.latitude),
                longitude: Number(newBenchForm.longitude),
                location: newBenchForm.location,
            });
            fetchAllBenches();
            setNewBenchForm({ bench_number: '', location: '', latitude: '', longitude: '' });
        } catch (err) {
            console.error('Error creating bench:', err);
        }
    };

    const handleUpdateBench = async () => {
        try {
            await updateBench(selectedBench._id, form);
            await refreshAndReselectBench(selectedBench._id);
            fetchAllBenches();
        } catch (err) {
            console.error('Error updating bench:', err);
        }
    };

    const handleDeleteBench = async () => {
        try {
            await deleteBench(selectedBench._id);
            setSelectedBench(null);
            fetchAllBenches();
        } catch (err) {
            console.error('Error deleting bench:', err);
        }
    };

    const refreshAndReselectBench = async (benchId) => {
        try {
            const { data } = await fetchBenches();
            setBenches(data);
            const updatedBench = data.find(b => b._id === benchId);
            setSelectedBench(updatedBench || null);
        } catch (err) {
            console.error('Failed to refresh bench:', err);
        }
    };


    return (
        <div className="p-4">
            <h1 id="role-manager-title">Bench Manager</h1>

            <Card className="mt-4 p-4 shadow-sm">
                <h2 id="create-new-bench">Create New Bench</h2>
                <Form>
                    <Form.Group controlId="newBenchNumber">
                        <Form.Label>Bench Number</Form.Label>
                        <Form.Control
                            name="bench_number"
                            type="number"
                            value={newBenchForm.bench_number}
                            onChange={handleNewBenchInputChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="newLocation">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                            name="location"
                            value={newBenchForm.location}
                            onChange={handleNewBenchInputChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="newLatitude">
                        <Form.Label>Latitude</Form.Label>
                        <Form.Control
                            name="latitude"
                            value={newBenchForm.latitude}
                            onChange={handleNewBenchInputChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="newLongitude">
                        <Form.Label>Longitude</Form.Label>
                        <Form.Control
                            name="longitude"
                            value={newBenchForm.longitude}
                            onChange={handleNewBenchInputChange}
                        />
                    </Form.Group>

                    <Button className="mt-3" onClick={handleCreateBench}>Create Bench</Button>
                </Form>
            </Card>

            <Card className="mt-4 p-4 shadow-sm">
                <h2 id="create-new-bench">Select a Bench</h2>
                <Dropdown onSelect={handleSelectBench}>
                    <Dropdown.Toggle variant="primary" id="dropdown-bench">
                        {selectedBench ? `Bench ${selectedBench.bench_number}` : 'Choose Bench'}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        {[...benches]
                            .sort((a, b) => a.bench_number - b.bench_number)
                            .map((bench) => (
                                <Dropdown.Item eventKey={bench._id} key={bench._id}>
                                    Bench #{bench.bench_number}
                                </Dropdown.Item>
                            ))}
                    </Dropdown.Menu>
                </Dropdown>

                {selectedBench && (
                    <div className="mt-4">
                        <h2 id="create-new-bench">Edit Bench</h2>
                        <Form>
                            <Form.Group controlId="formBenchNumber">
                                <Form.Label>Bench Number</Form.Label>
                                <Form.Control
                                    name="bench_number"
                                    type="number"
                                    value={form.bench_number}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group controlId="formLocation">
                                <Form.Label>Location</Form.Label>
                                <Form.Control
                                    name="location"
                                    value={form.location}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group controlId="formLatitude">
                                <Form.Label>Latitude</Form.Label>
                                <Form.Control
                                    name="latitude"
                                    value={form.latitude}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group controlId="formLongitude">
                                <Form.Label>Longitude</Form.Label>
                                <Form.Control
                                    name="longitude"
                                    value={form.longitude}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <div className="mt-3 d-flex gap-2 justify-content-center gap-3">
                                <Button 
                                    onClick={handleUpdateBench} 
                                    className="btn btn-blue modal-action-btn"
                                >
                                Update
                                </Button>
                                <Button
                                    variant="danger"
                                    className="modal-action-btn"
                                    onClick={() => setShowDeleteBenchModal(true)}
                                >
                                    <i className="fas fa-trash-alt me-1"></i>
                                    Delete
                                </Button>
                            </div>
                        </Form>
                    </div>
                )}
            </Card>
            <Modal show={showDeleteBenchModal} onHide={() => setShowDeleteBenchModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete bench #{selectedBench?.bench_number}?
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    <Button className="btn-cancel modal-action-btn" onClick={() => setShowDeleteBenchModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        className="modal-action-btn"
                        onClick={async () => {
                            await handleDeleteBench();
                            setShowDeleteBenchModal(false);
                        }}
                    >
                        <i className="fas fa-trash-alt me-1"></i>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BenchControlPanel;
